import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../services/api'; // ✅ FIXED: Use centralized API service
import './AdminVoterSurveys.css';

const AdminVoterSurveys = () => {
  const { t } = useTranslation();
  const [surveys, setSurveys] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDistrict, setSelectedDistrict] = useState('');
  const [selectedConstituency, setSelectedConstituency] = useState('');
  const [selectedCandidate, setSelectedCandidate] = useState('');
  const [districts, setDistricts] = useState([]);
  const [constituencies, setConstituencies] = useState([]);
  const [candidates, setCandidates] = useState([]);
  
  // View modal
  const [selectedSurvey, setSelectedSurvey] = useState(null);
  const [showModal, setShowModal] = useState(false);

  // ✅ FIXED: Remove API_BASE and use api service
  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    loadSurveys();
  }, [currentPage, pageSize]);

  const loadData = async () => {
    try {
      await Promise.all([
        loadSurveys(),
        loadStats(),
        loadDistricts(),
        loadConstituencies()
      ]);
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Failed to load data');
    }
  };

  const loadSurveys = async () => {
    try {
      setLoading(true);
      // ✅ FIXED: Use API service method
      const response = await api.getAllVoterSurveys(currentPage, pageSize);

      if (response.success) {
        setSurveys(response.data);
        setTotalPages(response.totalPages);
        setTotalCount(response.totalCount);
      }
    } catch (err) {
      console.error('❌ Error loading surveys:', err);
      setError('Failed to load surveys');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      // ✅ FIXED: Use API service method
      const response = await api.getVoterSurveyStats();
      if (response.success) {
        setStats(response.data);
      }
    } catch (err) {
      console.error('❌ Error loading stats:', err);
      // Don't set error, stats are optional
    }
  };

  const loadDistricts = async () => {
    try {
      // ✅ FIXED: Use API service method
      const response = await api.getSurveyDistricts();
      if (response.success) {
        setDistricts(response.data);
      }
    } catch (err) {
      console.error('❌ Error loading districts:', err);
    }
  };

  const loadConstituencies = async () => {
    try {
      // ✅ FIXED: Use API service method
      const response = await api.getAllConstituencies();
      if (response.success) {
        setConstituencies(response.data);
      }
    } catch (err) {
      console.error('❌ Error loading constituencies:', err);
    }
  };

  const loadCandidates = async (constituency) => {
    try {
      // ✅ FIXED: Use API service method
      const response = await api.getCandidatesByConstituencyName(constituency);
      if (response.success) {
        setCandidates(response.data);
      }
    } catch (err) {
      console.error('❌ Error loading candidates:', err);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      loadSurveys();
      return;
    }

    try {
      setLoading(true);
      // ✅ FIXED: Use API service method
      const response = await api.searchVoterSurveys(searchQuery);

      if (response.success) {
        setSurveys(response.data);
        setTotalCount(response.count);
      }
    } catch (err) {
      console.error('❌ Error searching:', err);
      setError('Search failed');
    } finally {
      setLoading(false);
    }
  };

  const filterByDistrict = async (district) => {
    if (!district) {
      loadSurveys();
      return;
    }

    try {
      setLoading(true);
      // ✅ FIXED: Use API service method
      const response = await api.getSurveysByDistrict(district, currentPage, pageSize);

      if (response.success) {
        setSurveys(response.data);
        setTotalPages(response.totalPages);
        setTotalCount(response.totalCount);
      }
    } catch (err) {
      console.error('❌ Error filtering by district:', err);
      setError('Filter failed');
    } finally {
      setLoading(false);
    }
  };

  const filterByConstituency = async (constituency) => {
    if (!constituency) {
      loadSurveys();
      return;
    }

    try {
      setLoading(true);
      // ✅ FIXED: Use API service method
      const response = await api.getSurveysByConstituency(constituency, currentPage, pageSize);

      if (response.success) {
        setSurveys(response.data);
        setTotalPages(response.totalPages);
        setTotalCount(response.totalCount);
      }
    } catch (err) {
      console.error('❌ Error filtering by constituency:', err);
      setError('Filter failed');
    } finally {
      setLoading(false);
    }
  };

  const filterByCandidate = async (candidate) => {
    if (!candidate) {
      loadSurveys();
      return;
    }

    try {
      setLoading(true);
      // ✅ FIXED: Use API service method
      const response = await api.getSurveysByCandidate(candidate, currentPage, pageSize);

      if (response.success) {
        setSurveys(response.data);
        setTotalPages(response.totalPages);
        setTotalCount(response.totalCount);
      }
    } catch (err) {
      console.error('❌ Error filtering by candidate:', err);
      setError('Filter failed');
    } finally {
      setLoading(false);
    }
  };

  const viewSurvey = async (surveyId) => {
    try {
      // ✅ FIXED: Use API service method
      const response = await api.getVoterSurveyById(surveyId);

      if (response.success) {
        setSelectedSurvey(response.data);
        setShowModal(true);
      }
    } catch (err) {
      console.error('❌ Error viewing survey:', err);
      alert('Failed to load survey details');
    }
  };

  const exportToCSV = () => {
    const headers = [
      'Survey ID',
      'Voter ID',
      'Full Name',
      'Email',
      'Phone',
      'Age',
      'Gender',
      'District',
      'Constituency',
      'Selected Candidate',
      'Main Concern',
      'Top Priority',
      'Government Performance',
      'Important Issue',
      'Preferred CM',
      'Expected Winner',
      'Submitted At',
      'IP Address'
    ];
    
    const rows = surveys.map(survey => [
      survey.surveyId,
      survey.voterId || '',
      survey.fullName,
      survey.email || '',
      survey.phoneNumber,
      survey.age,
      survey.gender,
      survey.district,
      survey.constituency,
      survey.selectedCandidate,
      survey.mainConcern || '',
      survey.topPriority || '',
      survey.governmentPerformance || '',
      survey.mostImportantIssue || '',
      survey.preferredChiefMinister || '',
      survey.expectedWinner || '',
      new Date(survey.submittedAt).toLocaleString(),
      survey.ipAddress || ''
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `voter-surveys-${Date.now()}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  if (loading && surveys.length === 0) {
    return (
      <div className="admin-voter-surveys-page">
        <div className="loading-container">
          <i className="fas fa-spinner fa-spin"></i>
          <p>Loading surveys...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-voter-surveys-page">
      {/* Header */}
      <div className="admin-survey-header">
        <h1>
          <i className="fas fa-poll"></i>
          Voter Surveys Management
        </h1>
        <p>Manage and analyze all voter survey responses</p>
      </div>

      {/* Statistics Cards */}
      {stats && (
        <div className="survey-stats-grid">
          <div className="survey-stat-card total">
            <div className="stat-icon">
              <i className="fas fa-poll-h"></i>
            </div>
            <div className="stat-content">
              <h3>Total Surveys</h3>
              <p className="stat-value">{stats.totalSurveys}</p>
            </div>
          </div>

          <div className="survey-stat-card gender">
            <div className="stat-icon">
              <i className="fas fa-venus-mars"></i>
            </div>
            <div className="stat-content">
              <h3>Gender Distribution</h3>
              <div className="gender-breakdown">
                {stats.byGender && stats.byGender.map((g, i) => (
                  <span key={i}>{g.gender}: {g.count}</span>
                ))}
              </div>
            </div>
          </div>

          <div className="survey-stat-card age">
            <div className="stat-icon">
              <i className="fas fa-users"></i>
            </div>
            <div className="stat-content">
              <h3>Average Age</h3>
              <p className="stat-value">{stats.averageAge ? stats.averageAge.toFixed(1) : 0} years</p>
            </div>
          </div>

          <div className="survey-stat-card top-candidate">
            <div className="stat-icon">
              <i className="fas fa-trophy"></i>
            </div>
            <div className="stat-content">
              <h3>Top Candidate</h3>
              <p className="stat-value-small">{stats.topCandidate?.name || 'N/A'}</p>
              <small>{stats.topCandidate?.votes || 0} votes</small>
            </div>
          </div>
        </div>
      )}

      {/* Filters and Search */}
      <div className="survey-filters-container">
        <div className="search-section">
          <input
            type="text"
            placeholder="Search by name, phone, email, survey ID..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
          />
          <button onClick={handleSearch} className="search-btn">
            <i className="fas fa-search"></i>
            Search
          </button>
        </div>

        <div className="filter-section">
          <select
            value={selectedDistrict}
            onChange={(e) => {
              setSelectedDistrict(e.target.value);
              filterByDistrict(e.target.value);
            }}
          >
            <option value="">All Districts</option>
            {districts.map((d, i) => (
              <option key={i} value={d.district}>
                {d.district} ({d.surveyCount})
              </option>
            ))}
          </select>

          <select
            value={selectedConstituency}
            onChange={(e) => {
              setSelectedConstituency(e.target.value);
              filterByConstituency(e.target.value);
              if (e.target.value) loadCandidates(e.target.value);
            }}
          >
            <option value="">All Constituencies</option>
            {constituencies.map((c, i) => (
              <option key={i} value={c.constituencyName}>
                {c.constituencyName}
              </option>
            ))}
          </select>

          <select
            value={selectedCandidate}
            onChange={(e) => {
              setSelectedCandidate(e.target.value);
              filterByCandidate(e.target.value);
            }}
            disabled={!selectedConstituency}
          >
            <option value="">All Candidates</option>
            {candidates.map((c, i) => (
              <option key={i} value={c.candidateName}>
                {c.candidateName} ({c.partyName})
              </option>
            ))}
          </select>

          <select
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
          >
            <option value="10">10 per page</option>
            <option value="20">20 per page</option>
            <option value="50">50 per page</option>
            <option value="100">100 per page</option>
          </select>

          <button onClick={exportToCSV} className="export-btn">
            <i className="fas fa-file-csv"></i>
            Export CSV
          </button>

          <button onClick={loadData} className="refresh-btn">
            <i className="fas fa-sync-alt"></i>
            Refresh
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="error-banner">
          <i className="fas fa-exclamation-triangle"></i>
          {error}
        </div>
      )}

      {/* Surveys Table */}
      <div className="survey-table-container">
        <div className="table-header-info">
          <h3>All Surveys ({totalCount})</h3>
          <p>Page {currentPage} of {totalPages}</p>
        </div>

        <table className="survey-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Survey ID</th>
              <th>Voter ID</th>
              <th>Full Name</th>
              <th>Phone</th>
              <th>Email</th>
              <th>Age</th>
              <th>Gender</th>
              <th>District</th>
              <th>Constituency</th>
              <th>Selected Candidate</th>
              <th>Main Concern</th>
              <th>Submitted</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {surveys.length === 0 ? (
              <tr>
                <td colSpan="14" className="no-data">
                  <i className="fas fa-inbox"></i>
                  <p>No surveys found</p>
                </td>
              </tr>
            ) : (
              surveys.map((survey, index) => (
                <tr key={survey.id || index}>
                  <td>{(currentPage - 1) * pageSize + index + 1}</td>
                  <td className="survey-id">{survey.surveyId}</td>
                  <td>{survey.voterId || '-'}</td>
                  <td className="name-cell">{survey.fullName}</td>
                  <td>{survey.phoneNumber}</td>
                  <td className="email-cell">{survey.email || '-'}</td>
                  <td>{survey.age}</td>
                  <td>
                    <span className={`gender-badge ${survey.gender?.toLowerCase()}`}>
                      {survey.gender}
                    </span>
                  </td>
                  <td>{survey.district}</td>
                  <td className="constituency-cell">{survey.constituency}</td>
                  <td className="candidate-cell">{survey.selectedCandidate}</td>
                  <td className="concern-cell">{survey.mainConcern || '-'}</td>
                  <td>{new Date(survey.submittedAt).toLocaleDateString()}</td>
                  <td>
                    <button
                      className="action-btn view"
                      onClick={() => viewSurvey(survey.surveyId)}
                      title="View Details"
                    >
                      <i className="fas fa-eye"></i>
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination-container">
          <button
            onClick={() => setCurrentPage(1)}
            disabled={currentPage === 1}
            className="page-btn"
          >
            <i className="fas fa-angle-double-left"></i>
          </button>
          <button
            onClick={() => setCurrentPage(currentPage - 1)}
            disabled={currentPage === 1}
            className="page-btn"
          >
            <i className="fas fa-angle-left"></i>
            Previous
          </button>

          <div className="page-numbers">
            {[...Array(Math.min(5, totalPages))].map((_, i) => {
              const pageNum = currentPage - 2 + i;
              if (pageNum > 0 && pageNum <= totalPages) {
                return (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    className={`page-num ${currentPage === pageNum ? 'active' : ''}`}
                  >
                    {pageNum}
                  </button>
                );
              }
              return null;
            })}
          </div>

          <button
            onClick={() => setCurrentPage(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="page-btn"
          >
            Next
            <i className="fas fa-angle-right"></i>
          </button>
          <button
            onClick={() => setCurrentPage(totalPages)}
            disabled={currentPage === totalPages}
            className="page-btn"
          >
            <i className="fas fa-angle-double-right"></i>
          </button>
        </div>
      )}

      {/* View Modal */}
      {showModal && selectedSurvey && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                <i className="fas fa-poll"></i>
                Survey Details
              </h2>
              <button onClick={() => setShowModal(false)} className="modal-close">
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="modal-body">
              <div className="survey-details-grid">
                <div className="detail-section">
                  <h4>Personal Information</h4>
                  <div className="detail-item">
                    <label>Survey ID:</label>
                    <span>{selectedSurvey.surveyId}</span>
                  </div>
                  <div className="detail-item">
                    <label>Voter ID:</label>
                    <span>{selectedSurvey.voterId || 'Not provided'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Full Name:</label>
                    <span>{selectedSurvey.fullName}</span>
                  </div>
                  <div className="detail-item">
                    <label>Email:</label>
                    <span>{selectedSurvey.email || 'Not provided'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Phone:</label>
                    <span>{selectedSurvey.phoneNumber}</span>
                  </div>
                  <div className="detail-item">
                    <label>Age:</label>
                    <span>{selectedSurvey.age} years</span>
                  </div>
                  <div className="detail-item">
                    <label>Gender:</label>
                    <span>{selectedSurvey.gender}</span>
                  </div>
                </div>

                <div className="detail-section">
                  <h4>Location & Voting</h4>
                  <div className="detail-item">
                    <label>District:</label>
                    <span>{selectedSurvey.district}</span>
                  </div>
                  <div className="detail-item">
                    <label>Constituency:</label>
                    <span>{selectedSurvey.constituency}</span>
                  </div>
                  <div className="detail-item">
                    <label>Selected Candidate:</label>
                    <span className="highlight">{selectedSurvey.selectedCandidate}</span>
                  </div>
                </div>

                <div className="detail-section full-width">
                  <h4>Survey Responses</h4>
                  <div className="detail-item">
                    <label>Main Concern:</label>
                    <span>{selectedSurvey.mainConcern || 'Not provided'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Top Priority:</label>
                    <span>{selectedSurvey.topPriority || 'Not provided'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Government Performance:</label>
                    <span>{selectedSurvey.governmentPerformance || 'Not provided'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Most Important Issue:</label>
                    <span>{selectedSurvey.mostImportantIssue || 'Not provided'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Preferred Chief Minister:</label>
                    <span>{selectedSurvey.preferredChiefMinister || 'Not provided'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Expected Winner:</label>
                    <span>{selectedSurvey.expectedWinner || 'Not provided'}</span>
                  </div>
                </div>

                <div className="detail-section full-width">
                  <h4>Metadata</h4>
                  <div className="detail-item">
                    <label>Submitted At:</label>
                    <span>{new Date(selectedSurvey.submittedAt).toLocaleString()}</span>
                  </div>
                  <div className="detail-item">
                    <label>IP Address:</label>
                    <span>{selectedSurvey.ipAddress || 'Unknown'}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button onClick={() => setShowModal(false)} className="btn-secondary">
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminVoterSurveys;
